import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "quowin_auth_token";

export const api = axios.create({ baseURL: API, timeout: 180000 });

/* ── Auth token attach ── */
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ── Global 401 handler: except on login endpoint (bad-password 401 is user-facing) ── */
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.includes("/auth/login")) {
      // Session expired or invalid — clear and redirect.
      localStorage.removeItem(TOKEN_KEY);
      // Preserve where user was trying to go.
      const here = window.location.pathname + window.location.search;
      if (!window.location.pathname.startsWith("/login")) {
        const next = here && here !== "/login" ? `?next=${encodeURIComponent(here)}` : "";
        window.location.replace(`/login${next}`);
      }
    }
    return Promise.reject(error);
  }
);

/* ── Proposals & templates ── */
export const listProposals = () => api.get("/proposals").then((r) => r.data);
export const getProposal = (id) => api.get(`/proposals/${id}`).then((r) => r.data);
export const generateProposal = (transcript, template_id) =>
  api.post("/proposals/generate", { transcript, template_id }).then((r) => r.data);
export const listTemplates = () => api.get("/templates").then((r) => r.data);
export const createTemplate = (t) => api.post("/templates", t).then((r) => r.data);
export const deleteTemplate = (id) => api.delete(`/templates/${id}`).then((r) => r.data);
export const updateProposal = (id, patch) =>
  api.patch(`/proposals/${id}`, patch).then((r) => r.data);
export const duplicateProposal = (id) =>
  api.post(`/proposals/${id}/duplicate`).then((r) => r.data);
export const deleteProposal = (id) =>
  api.delete(`/proposals/${id}`).then((r) => r.data);
export const copilotAction = (id, payload) =>
  api.post(`/proposals/${id}/copilot`, payload).then((r) => r.data);
export const draftEmail = (id) =>
  api.post(`/proposals/${id}/email/draft`).then((r) => r.data);
export const sendEmail = (id, payload) =>
  api.post(`/proposals/${id}/email/send`, payload).then((r) => r.data);
export const uploadAttachment = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/proposals/${id}/attachments`, fd).then((r) => r.data);
};
export const deleteAttachment = (id, aid) =>
  api.delete(`/proposals/${id}/attachments/${aid}`).then((r) => r.data);
export const uploadTranscript = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/transcripts/upload`, fd).then((r) => r.data);
};
export const pdfUrl = (id) => `${API}/proposals/${id}/export/pdf`;
export const docxUrl = (id) => `${API}/proposals/${id}/export/docx`;
export const simulateOpen = (id) =>
  api.post(`/proposals/${id}/simulate-open`).then((r) => r.data);

/* ── Auth ── */
export const authLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authPatchMe = (patch) => api.patch("/auth/me", patch).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);

/* ── Integration scaffolding ── */
export const getIntegrationSettings = () => api.get("/integrations/settings").then((r) => r.data);
export const updateIntegrationSettings = (patch) =>
  api.patch("/integrations/settings", patch).then((r) => r.data);
